/**
 * Context-aware extraction, Phase 1 (YAR-493): turns a hinted review item
 * plus the schedule item it points at into what the Review card renders.
 *
 * Pure — no Supabase, no React — so every card state is unit-testable.
 *
 * The web never APPLIES a proposal. On Confirm, `approve_email` does the
 * partial update / cancel. When the target is gone, an UPDATE is inserted as
 * a new item, while a CANCEL does nothing at all (a cancel never becomes a
 * new item). This module only decides how to describe that to the parent.
 */
import { formatLongDateRange, formatTime, formatTimeRange } from '@/lib/date';
import { matchFamilyChild } from '@/lib/familyChild';
import type { ProposalTarget } from '@/services/email.service';
import type { Child, ReviewItem } from '@/types';

export type ProposalAction = 'update' | 'cancel';

export type ProposalView =
  /** No hint, or the target no longer exists: render the ordinary card. */
  | { kind: 'none' }
  /** A hint we could verify against a live target. */
  | { kind: 'resolved'; action: ProposalAction; target: ProposalTarget }
  /**
   * A hint whose target could not be READ (network / query error). Confirm
   * would still act on the existing item, so the card must not pretend the
   * item is new; it says "a change to something on your schedule" instead.
   */
  | { kind: 'unverified'; action: ProposalAction }
  /**
   * A cancel with nothing left to cancel: the target was deleted, is not in
   * this household, or is a task already done / cancelled. approve_email
   * skips it (inserts nothing), so the card says there is nothing to do.
   */
  | { kind: 'gone'; action: 'cancel' };

function hintedAction(item: ReviewItem): ProposalAction | null {
  if (!item.update_hint) return null;
  if (item.proposed_action !== 'update' && item.proposed_action !== 'cancel')
    return null;
  return item.proposed_action;
}

/** The target id the hint points at, whichever kind it is. */
function hintedTargetId(item: ReviewItem): string | null {
  return item.supersedes_event_id ?? item.supersedes_task_id ?? null;
}

const GONE: ProposalView = { kind: 'gone', action: 'cancel' };

export function resolveProposal(
  item: ReviewItem,
  targets: Map<string, ProposalTarget>,
  targetsFailed = false,
): ProposalView {
  const action = hintedAction(item);
  if (!action) return { kind: 'none' };
  const targetId = hintedTargetId(item);
  // Dangling hint (FK set to null when the target was deleted, or never
  // set): approve_email inserts an update as a new item, so it renders as
  // one — and skips a cancel, so that says there is nothing to do.
  if (!targetId) return action === 'cancel' ? GONE : { kind: 'none' };
  const target = targets.get(targetId);
  if (target) {
    // An UPDATE copies fields onto the target, so it only applies to a
    // target of the item's own kind (verifyMatch, record_extraction_result
    // and approve_email all agree); approve_email inserts a mismatched one
    // as new, so it renders as new. A cancel may take the target's kind.
    if (action === 'update' && target.kind !== item.type) {
      return { kind: 'none' };
    }
    if (action === 'cancel') {
      // Mirrors approve_email: only an OPEN task can be cancelled.
      if (
        target.kind === 'task' &&
        target.taskStatus !== 'TODO' &&
        target.taskStatus !== 'IN_PROGRESS'
      ) {
        return GONE;
      }
      return { kind: 'resolved', action, target };
    }
    return { kind: 'resolved', action, target };
  }
  if (targetsFailed) return { kind: 'unverified', action };
  // Read fine, target absent: deleted since (or not this household's).
  return action === 'cancel' ? GONE : { kind: 'none' };
}

// =============================================================================
// Before → after diff
// =============================================================================

export type ProposalField =
  | 'title'
  | 'date'
  | 'time'
  | 'endTime'
  | 'location'
  | 'children';

export interface ProposalChange {
  field: ProposalField;
  /** null = the target had no value ("No time", "No location", …). */
  before: string | null;
  after: string;
}

const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').trim() === (b ?? '').trim();

function childNames(ids: string[], children: Child[]): string | null {
  const byId = new Map(children.map((c) => [c.id, c.name]));
  const names = ids.map((id) => byId.get(id)).filter((n): n is string => !!n);
  return names.length > 0 ? names.join(', ') : null;
}

/**
 * The children approval will actually write, mirroring emails-approve's
 * `resolveChildIds` (minus the modal-level fallback pick). The Review modal
 * edits `child_names` only, so once every current name resolves, those
 * names ARE the parent's choice and win over the extraction's `child_ids`
 * (equal when untouched, the correction when not, empty when every child
 * was removed). A name that no longer resolves cannot express a choice, and
 * the extraction's ids stand.
 */
export function effectiveChildIds(
  item: ReviewItem,
  children: Child[],
): string[] {
  const names = item.child_names ?? [];
  const resolved = names.map((n) => matchFamilyChild(n, children)?.id);
  const ids = item.child_ids ?? [];
  if (ids.length > 0) {
    return resolved.every((id): id is string => typeof id === 'string')
      ? [...new Set(resolved)]
      : [...new Set(ids)];
  }
  const legacy =
    names.length > 0
      ? resolved
      : item.child_name
        ? [matchFamilyChild(item.child_name, children)?.id]
        : [];
  return [...new Set(legacy.filter((id): id is string => !!id))];
}

/**
 * Only the fields the new email actually spoke to AND that differ from the
 * target. A null/empty field on the review item means "not mentioned", which
 * approve_email leaves untouched — so it is never shown as a change.
 *
 * Dates and times are compared as the strings the parent will read, in the
 * same zone, so the diff can never claim a change the two sides print
 * identically (or hide one they print differently).
 */
export function diffProposal(
  item: ReviewItem,
  target: ProposalTarget,
  children: Child[],
  timezone?: string | null,
): ProposalChange[] {
  const changes: ProposalChange[] = [];

  if (item.title.trim() && !same(item.title, target.title)) {
    changes.push({
      field: 'title',
      before: target.title,
      after: item.title.trim(),
    });
  }

  const newStart =
    target.kind === 'event'
      ? (item.start_datetime ?? item.due_datetime)
      : (item.due_datetime ?? item.start_datetime);

  if (newStart) {
    const day = (iso: string) => formatLongDateRange(iso, null, timezone);
    const after = day(newStart);
    const before = target.start ? day(target.start) : null;
    if (after !== before) changes.push({ field: 'date', before, after });

    // A date-only extraction is stored at local noon as a day-boundary
    // guard; comparing that invented hour would claim a time change the
    // email never made (YAR-376 / YAR-378). approve_email agrees: it moves
    // only the DATE of a timed target and keeps its time of day.
    if (!item.is_time_unknown) {
      const targetTimed = !!target.start && !target.isTimeUnknown;
      const newEnd = target.kind === 'event' ? item.end_datetime : null;
      const targetEnd = targetTimed ? target.end : null;
      const startAfter = formatTime(newStart, timezone);
      const startBefore = targetTimed
        ? formatTime(target.start as string, timezone)
        : null;
      if (startAfter !== startBefore) {
        // The start moved: show the whole range on both sides, so an end
        // that simply moved along with it is not listed as a second change.
        changes.push({
          field: 'time',
          before: targetTimed
            ? formatTimeRange(target.start as string, targetEnd, timezone)
            : null,
          after: formatTimeRange(newStart, newEnd, timezone),
        });
      } else if (newEnd) {
        const endAfter = formatTime(newEnd, timezone);
        const endBefore = targetEnd ? formatTime(targetEnd, timezone) : null;
        if (endAfter !== endBefore) {
          changes.push({
            field: 'endTime',
            before: endBefore,
            after: endAfter,
          });
        }
      }
    }
  }

  if (item.location?.trim() && !same(item.location, target.location)) {
    changes.push({
      field: 'location',
      before: target.location?.trim() || null,
      after: item.location.trim(),
    });
  }

  // What approval writes: the parent's edited names win (see
  // effectiveChildIds). Empty = "not mentioned" or "all removed", both of
  // which approve_email leaves untouched on an update.
  const newChildIds = effectiveChildIds(item, children);
  if (newChildIds.length > 0) {
    const sortedNew = [...new Set(newChildIds)].sort();
    const sortedOld = [...new Set(target.childIds)].sort();
    const differs =
      sortedNew.length !== sortedOld.length ||
      sortedNew.some((id, i) => id !== sortedOld[i]);
    const after = childNames(sortedNew, children);
    if (differs && after) {
      changes.push({
        field: 'children',
        before: childNames(sortedOld, children),
        after,
      });
    }
  }

  return changes;
}

/** "Tue, Oct 14" for the cancel card; null when the target is undated. */
export function targetDateLabel(
  target: ProposalTarget,
  timezone?: string | null,
): string | null {
  if (!target.start) return null;
  return formatLongDateRange(target.start, target.end, timezone);
}
